import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import EditablePageIntro from '../components/EditablePageIntro';
import { Plus, MessageSquare, Clock, User, Search, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
    { id: 'general', name: 'General' },
    { id: 'consciousness', name: 'Consciousness' },
    { id: 'practice', name: 'Practice' },
    { id: 'experiences', name: 'Experiences' },
    { id: 'resources', name: 'Resources' }
];

export default function ForumPage() {
    const { user } = useAuth();
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [newPostOpen, setNewPostOpen] = useState(false);
    const [newPost, setNewPost] = useState({ title: '', content: '', category: 'general' });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        fetchPosts();
    }, [selectedCategory]);

    const fetchPosts = async () => {
        try {
            const params = new URLSearchParams();
            if (selectedCategory) params.append('category', selectedCategory);
            
            const { data } = await axios.get(`${API_URL}/api/forum/posts?${params}`, { withCredentials: true });
            setPosts(data);
        } catch (error) {
            console.error('Error fetching posts:', error);
            toast.error('Failed to load discussions');
        } finally {
            setLoading(false);
        }
    };

    const handleCreatePost = async (e) => {
        e.preventDefault();
        if (!newPost.title.trim() || !newPost.content.trim()) {
            toast.error('Please fill in all fields');
            return;
        }

        setSubmitting(true);
        try {
            await axios.post(`${API_URL}/api/forum/posts`, newPost, { withCredentials: true });
            toast.success('Discussion created successfully');
            setNewPostOpen(false);
            setNewPost({ title: '', content: '', category: 'general' });
            fetchPosts();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to create discussion');
        } finally {
            setSubmitting(false);
        }
    };

    const canModerate = user?.role === 'admin' || user?.membership_tier === 'partnership';

    const handleDeletePost = async (e, postId) => {
        e.preventDefault();
        e.stopPropagation();
        if (!window.confirm('Delete this discussion and all of its replies?')) return;
        try {
            await axios.delete(`${API_URL}/api/forum/posts/${postId}`, { withCredentials: true });
            toast.success('Discussion deleted');
            fetchPosts();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to delete');
        }
    };

    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        const now = new Date();
        const diffMs = now - date;
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        
        if (diffDays === 0) return 'Today';
        if (diffDays === 1) return 'Yesterday';
        if (diffDays < 7) return `${diffDays} days ago`;
        return date.toLocaleDateString();
    };

    const filteredPosts = posts.filter(post => 
        post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.content.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const getTierBadgeClass = (tier) => {
        switch (tier) {
            case 'seeker': return 'tier-badge tier-seeker';
            case 'walker': return 'tier-badge tier-walker';
            case 'architect': return 'tier-badge tier-architect';
            case 'partnership': return 'tier-badge tier-partnership';
            default: return 'tier-badge bg-gray-100 text-gray-600';
        }
    };

    return (
        <div className="min-h-screen bg-[#F9F6F0]">
            <Navbar />

            <main className="pt-24 pb-16" data-testid="forum-page">
                <div className="max-w-5xl mx-auto px-6 sm:px-12">
                    {/* Header */}
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-8">
                        <div className="flex-1">
                            <p className="overline mb-2">Community</p>
                            <h1 className="heading-2">Forum</h1>
                            <EditablePageIntro
                                pageKey="forum"
                                className="mt-3 max-w-2xl"
                                fallback={null}
                            />
                        </div>
                        
                        <Dialog open={newPostOpen} onOpenChange={setNewPostOpen}>
                            <DialogTrigger asChild>
                                <button className="btn-primary flex items-center gap-2" data-testid="new-post-btn">
                                    <Plus className="w-4 h-4" />
                                    New Discussion
                                </button>
                            </DialogTrigger>
                            <DialogContent className="sm:max-w-lg bg-white">
                                <DialogHeader>
                                    <DialogTitle className="font-serif text-2xl text-[#1A1A2E]">Start a Discussion</DialogTitle>
                                </DialogHeader>
                                <form onSubmit={handleCreatePost} className="space-y-4 mt-4" data-testid="new-post-form">
                                    <div>
                                        <label className="block font-sans text-sm text-[#1A1A2E] mb-2">Title</label>
                                        <input
                                            type="text"
                                            value={newPost.title}
                                            onChange={(e) => setNewPost({ ...newPost, title: e.target.value })}
                                            placeholder="What's on your mind?"
                                            className="w-full"
                                            required
                                            data-testid="new-post-title"
                                        />
                                    </div>
                                    <div>
                                        <label className="block font-sans text-sm text-[#1A1A2E] mb-2">Category</label>
                                        <select
                                            value={newPost.category}
                                            onChange={(e) => setNewPost({ ...newPost, category: e.target.value })}
                                            className="w-full bg-white border border-[#E5E0D8] px-4 py-3 rounded-sm"
                                            data-testid="new-post-category"
                                        >
                                            {CATEGORIES.map(cat => (
                                                <option key={cat.id} value={cat.id}>{cat.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block font-sans text-sm text-[#1A1A2E] mb-2">Content</label>
                                        <textarea
                                            value={newPost.content}
                                            onChange={(e) => setNewPost({ ...newPost, content: e.target.value })}
                                            placeholder="Share your thoughts..."
                                            rows={5}
                                            className="w-full resize-none"
                                            required
                                            data-testid="new-post-content"
                                        />
                                    </div>
                                    <button
                                        type="submit"
                                        className="btn-primary w-full"
                                        disabled={submitting}
                                        data-testid="submit-post-btn"
                                    >
                                        {submitting ? 'Creating...' : 'Create Discussion'}
                                    </button>
                                </form>
                            </DialogContent>
                        </Dialog>
                    </div>

                    {/* Filters */}
                    <div className="flex flex-col sm:flex-row gap-4 mb-8">
                        {/* Search */}
                        <div className="relative flex-grow">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4A4A68]" />
                            <input
                                type="text"
                                placeholder="Search discussions..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-12"
                                data-testid="forum-search"
                            />
                        </div>
                        
                        {/* Category Filter */}
                        <div className="flex gap-2 flex-wrap">
                            <button
                                onClick={() => setSelectedCategory('')}
                                className={`px-4 py-2 rounded-sm text-sm font-sans transition-all ${
                                    !selectedCategory 
                                        ? 'bg-[#5B2D8E] text-white' 
                                        : 'bg-white border border-[#E5E0D8] text-[#4A4A68] hover:border-[#5B2D8E]'
                                }`}
                                data-testid="filter-all"
                            >
                                All
                            </button>
                            {CATEGORIES.map(cat => (
                                <button
                                    key={cat.id}
                                    onClick={() => setSelectedCategory(cat.id)}
                                    className={`px-4 py-2 rounded-sm text-sm font-sans transition-all ${
                                        selectedCategory === cat.id 
                                            ? 'bg-[#5B2D8E] text-white' 
                                            : 'bg-white border border-[#E5E0D8] text-[#4A4A68] hover:border-[#5B2D8E]'
                                    }`}
                                    data-testid={`filter-${cat.id}`}
                                >
                                    {cat.name}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Posts List */}
                    {loading ? (
                        <div className="text-center py-12">
                            <div className="w-12 h-12 border-4 border-[#5B2D8E] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                            <p className="text-[#4A4A68]">Loading discussions...</p>
                        </div>
                    ) : filteredPosts.length > 0 ? (
                        <div className="space-y-4" data-testid="posts-list">
                            {filteredPosts.map(post => (
                                <Link
                                    key={post.id}
                                    to={`/forum/${post.id}`}
                                    className="content-card block hover:border-[#5B2D8E] transition-colors"
                                    data-testid={`post-${post.id}`}
                                >
                                    <div className="flex items-start justify-between gap-4">
                                        <div className="flex-grow">
                                            <div className="flex items-center gap-2 mb-2">
                                                <span className="px-2 py-1 bg-[#F2EBE5] text-[#5B2D8E] text-xs rounded-sm capitalize">
                                                    {post.category}
                                                </span>
                                            </div>
                                            <h3 className="font-serif text-xl text-[#1A1A2E] mb-2">{post.title}</h3>
                                            <p className="font-sans text-sm text-[#4A4A68] line-clamp-2 mb-4">
                                                {post.content}
                                            </p>
                                            <div className="flex items-center gap-4 text-sm text-[#4A4A68]">
                                                <span className="flex items-center gap-1">
                                                    <User className="w-4 h-4" />
                                                    {post.author_name}
                                                </span>
                                                <span className={getTierBadgeClass(post.author_tier)}>
                                                    {post.author_tier}
                                                </span>
                                                <span className="flex items-center gap-1">
                                                    <Clock className="w-4 h-4" />
                                                    {formatDate(post.created_at)}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 text-[#5B2D8E]">
                                            <MessageSquare className="w-5 h-5" />
                                            <span className="font-sans text-sm">{post.reply_count || 0}</span>
                                            {(canModerate || post.author_id === user?.id) && (
                                                <button
                                                    onClick={(e) => handleDeletePost(e, post.id)}
                                                    className="ml-2 p-2 hover:bg-red-50 rounded"
                                                    data-testid={`delete-post-${post.id}`}
                                                    title="Delete discussion"
                                                >
                                                    <Trash2 className="w-4 h-4 text-red-500" />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-12 bg-white border border-[#E5E0D8] rounded-sm" data-testid="no-posts">
                            <MessageSquare className="w-12 h-12 text-[#D8B4E2] mx-auto mb-4" />
                            <h3 className="font-serif text-xl text-[#1A1A2E] mb-2">No discussions yet</h3>
                            <p className="text-[#4A4A68] mb-6">Be the first to start a conversation!</p>
                            <button 
                                onClick={() => setNewPostOpen(true)}
                                className="btn-primary"
                            >
                                Start a Discussion
                            </button>
                        </div>
                    )}
                </div>
            </main>

            <Footer />
        </div>
    );
}
